import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import * as Yup from 'yup'
import { Row, Col } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import * as jose from 'jose'
import { useTranslation } from 'react-i18next'
import {
  Typography,
  InfoCard,
  UploadPageBackground,
  UploadStickyHeader,
  UploadHeaderIconBox,
  UploadDivider,
  UploadInfoPanel,
  UploadInitialsCircle,
  UploadCalendarCircle,
  UploadAttachmentsBox,
  UploadAttachmentItem,
  UploadAttachmentDownloadLink,
  UploadLoadingWrapper,
} from '../components/styled'
import storageApi from '../services/firebaseStorage'
import apiCandidates from '../services/api/localCandidates'
import apiTokens from '../services/api/token'
import NotificationBoxPage from '../components/notificationBoxPage'
import ProgressBar from '../components/progress-bar/progressBar'
import FullLogo from '../assets/logos/full-logo.png'
import PrimaryButton from '../components/buttons/PrimaryButton'
import theme from '../constants/theme'
import AbstractForm from '../components/forms/AbstractForm'
import LanguagesDropdown from '../components/languageDropdown'
import {
  KEY_SUPPORTED_FORMATS,
  MAX_FILE_SIZE,
} from '../utilities/stringUtilities'
import FileText from '../assets/svgs/FileText'
import ExclamationCircle from '../assets/svgs/exclamationCircle'
import Download from '../assets/svgs/Download'
import CalendarSquare from '../assets/svgs/calendarSquare'

function UploadRequestedDocuments() {
  const { t } = useTranslation()
  const { token } = useParams()

  const [isRenderForm, setIsRenderForm] = useState(true)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [showNotification, setShowNotification] = useState(false)
  const [submittedDocuments, setSubmittedDocuments] = useState([])
  const [attachments, setAttachments] = useState([])
  const [documentSubmissionDeadline, setDocumentSubmissionDeadline] =
    useState('')
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true)

  const progressRef = useRef(null)
  const errorRef = useRef(null)
  const [stopMultipleUploadFormLoading, setStopMultipleUploadFormLoading] =
    useState(false)

  let decodedToken = null
  try {
    decodedToken = jose.decodeJwt(token)
  } catch {
    decodedToken = null
  }

  const {
    documents = [],
    candidate = {},
    company = {},
    exp = 0,
  } = decodedToken || {}

  useEffect(() => {
    const fetchSubmittedDocuments = async () => {
      try {
        const response = await apiCandidates.getDocumentsByToken(
          candidate.id,
          token,
        )
        const responseData = response.data || response
        const documentsList = responseData.documents || []
        const submittedDocs = documentsList.map((doc) => doc.name)
        setSubmittedDocuments(submittedDocs)
        setAttachments(responseData.attachments || [])
      } catch {
        setSubmittedDocuments([])
        setAttachments([])
      } finally {
        setIsLoadingDocuments(false)
      }
    }

    fetchSubmittedDocuments()
  }, [candidate.id, token])

  const checkDocumentDeadline = (document) => {
    if (!document.deadline) {
      return { isPassed: false, deadline: null }
    }

    const deadline = new Date(document.deadline)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    deadline.setHours(0, 0, 0, 0)

    const isPassed = today > deadline

    return {
      isPassed,
      deadline: document.deadline,
    }
  }

  const isDocumentSubmitted = (documentLabel) => {
    return submittedDocuments.includes(documentLabel)
  }

  const inputs = useMemo(() => {
    return documents.map((document, index) => {
      const deadlineCheck = checkDocumentDeadline(document)
      const isSubmitted = isDocumentSubmitted(document.label)
      const isDisabled = deadlineCheck.isPassed || isSubmitted

      let statusText = ''
      if (isSubmitted) {
        statusText = ` - ✓ ${t('positionForm.documentUpload.submitted')}`
      } else if (deadlineCheck.isPassed) {
        statusText = ` - ${t('positionForm.documentUpload.expired')}`
      }

      const deadlineText = document.deadline
        ? ` - ${t('general.deadline')}: ${document.deadline}`
        : ''

      let placeholder = t('positionForm.documentUpload.uploadYourDocument', {
        label: document.label,
      })
      let helperText = null

      if (isSubmitted) {
        placeholder = t('positionForm.documentUpload.alreadySubmitted')
        helperText = t('positionForm.documentUpload.documentAlreadyUploaded')
      } else if (deadlineCheck.isPassed) {
        placeholder = t('positionForm.documentUpload.deadlinePassed')
        helperText = t('positionForm.documentUpload.cannotUploadAfterDeadline')
      }

      return {
        name: `document${index}`,
        label: `${document.label} (${document.format?.toUpperCase()} ${t('positionForm.documentUpload.format')})${!isSubmitted ? deadlineText : ''}${statusText}`,
        renderType: 'dropzone',
        width: 12,
        accept: document.format,
        required: false,
        placeholder,
        disabled: isDisabled,
        helperText,
      }
    })
  }, [submittedDocuments, documents, t])

  const schema = useMemo(() => {
    const yupObject = {}
    documents.forEach((document, index) => {
      const deadlineCheck = checkDocumentDeadline(document)
      const isSubmitted = isDocumentSubmitted(document.label)

      if (deadlineCheck.isPassed || isSubmitted) {
        yupObject[`document${index}`] = Yup.mixed()
          .notRequired()
          .default(undefined)
        return
      }

      yupObject[`document${index}`] = Yup.mixed()
        .notRequired()
        .test(
          'fileUpload',
          t('positionForm.documentUpload.oneFilePerDocument'),
          (value) => {
            if (!value) return true
            if (value instanceof FileList) return value.length <= 1
            return Object.keys(value).length <= 1
          },
        )
        .test(
          'fileSize',
          t('positionForm.documentUpload.fileSizeExceeded'),
          (files) => {
            if (!files) return true
            if (files instanceof FileList) {
              return Array.from(files).every(
                (file) => file.size <= MAX_FILE_SIZE,
              )
            }
            return Object.keys(files).every(
              (file) => files[file].size <= MAX_FILE_SIZE,
            )
          },
        )
        .test(
          'fileFormat',
          t('positionForm.documentUpload.pleaseUploadCorrectFormat', {
            format: document.format?.toUpperCase(),
          }),
          (files) => {
            if (!files) return true
            if (files instanceof FileList) {
              return Array.from(files).every(
                (file) => file.type === KEY_SUPPORTED_FORMATS[document.format],
              )
            }
            return Object.keys(files).every(
              (file) =>
                files[file].type === KEY_SUPPORTED_FORMATS[document.format],
            )
          },
        )
    })

    return Yup.object().shape(yupObject)
  }, [submittedDocuments, documents, t])

  const handleUploadProgress = useCallback((node, progress) => {
    const progressNode = node
    progressNode.setAttribute('aria-valuenow', progress)
    progressNode.setAttribute(
      'style',
      `width: ${progress}%; background-color: ${theme.talentinoBlue}`,
    )
    progressNode.innerText = `${progress}%`
  }, [])

  const handleMultipleUploadProgress = useCallback(
    (progress) => {
      if (progressRef.current) {
        handleUploadProgress(progressRef.current, progress)
      }
    },
    [handleUploadProgress],
  )

  const handleSuccessfulUpload = useCallback(
    async (data, setIsLoading) => {
      try {
        const res = await apiCandidates.updateDocumentsWithToken(
          candidate.id,
          data,
        )
        if (res) {
          const newlySubmittedDocs = data.documents.map((doc) => doc.name)
          const updatedSubmittedDocs = [
            ...submittedDocuments,
            ...newlySubmittedDocs,
          ]
          setSubmittedDocuments(updatedSubmittedDocs)

          const allComplete = documents.every(
            (doc) =>
              checkDocumentDeadline(doc).isPassed ||
              updatedSubmittedDocs.includes(doc.label),
          )

          setMessage(t('positionForm.documentUpload.filesUploadedSuccessfully'))
          setMessageType('success')
          setShowNotification(true)

          if (allComplete) {
            setIsRenderForm(false)
          } else {
            setIsRenderForm(true)
          }
        }
      } catch {
        setMessage(t('positionForm.documentUpload.uploadErrorRefresh'))
        setMessageType('error')
        setShowNotification(true)
        setIsRenderForm(false)
      } finally {
        setIsLoading(false)
        setStopMultipleUploadFormLoading(true)
      }
    },
    [candidate.id, submittedDocuments, documents, t],
  )

  const handleMultipleUploadError = useCallback(
    (rejected) => {
      if (rejected.length > 0) {
        const errorMessage = document.createElement('code')
        errorMessage.innerText = t('positionForm.documentUpload.failedToUpload')
        errorRef.current.appendChild(errorMessage)
        errorRef.current.appendChild(document.createElement('br'))
        const errorFiles = document.createElement('code')
        rejected.forEach((name, index) => {
          errorFiles.innerText =
            index === 0 ? name : `${errorFiles.innerText}, ${name}`
        })
        errorRef.current.appendChild(errorFiles)
      }
    },
    [t],
  )

  const handleSubmit = useCallback(
    (data, setIsLoading) => {
      const activeDocumentIndices = documents
        .map((doc, index) => ({ doc, index }))
        .filter(
          ({ doc }) =>
            !checkDocumentDeadline(doc).isPassed &&
            !isDocumentSubmitted(doc.label),
        )
        .map(({ index }) => index)

      if (activeDocumentIndices.length === 0) {
        setMessage(t('positionForm.documentUpload.allDocumentsSubmitted'))
        setMessageType('success')
        setShowNotification(true)
        setIsRenderForm(false)
        setIsLoading(false)
        return
      }

      const uploads = Object.keys(data)
        .filter((key) => {
          const docIndex = parseInt(key.replace('document', ''))
          return (
            activeDocumentIndices.includes(docIndex) &&
            data[key] &&
            (data[key] instanceof FileList
              ? data[key].length > 0
              : Object.keys(data[key]).length > 0)
          )
        })
        .map((key) => {
          let file
          if (data[key] instanceof FileList) {
            file = data[key][0]
          } else {
            const fileKey = Object.keys(data[key])[0]
            file = data[key][fileKey]
          }
          return { key, file }
        })

      if (uploads.length === 0) {
        setMessage(
          t('positionForm.documentUpload.pleaseSelectAtLeastOneDocument'),
        )
        setMessageType('error')
        setShowNotification(true)
        setIsLoading(false)
        return
      }

      const submitData = {
        companyId: company.id,
        token,
      }
      const resumePromises = []
      uploads.forEach(({ file }) => {
        resumePromises.push(
          storageApi.uploadFile(
            file,
            handleMultipleUploadProgress,
            () => {}, // onSuccess callback (not used here as we handle all in Promise.allSettled)
            () => {}, // onError callback (not used here as we handle all in Promise.allSettled)
          ),
        )
      })
      Promise.allSettled(resumePromises).then((results) => {
        const fulfilled = []
        const rejected = []
        results.forEach((result, index) => {
          const originalIndex = parseInt(
            uploads[index].key.replace('document', ''),
          )
          if (result.status === 'fulfilled')
            fulfilled.push({
              url: result.value,
              name: documents[originalIndex].label,
              is_resume: false,
            })
          else if (result.status === 'rejected')
            rejected.push(uploads[index].file.name)
        })
        handleSuccessfulUpload(
          {
            ...submitData,
            documents: fulfilled,
          },
          setIsLoading,
        )
        handleMultipleUploadError(rejected)
        setIsLoading(false)
      })
    },
    [
      documents,
      submittedDocuments,
      t,
      company.id,
      token,
      handleSuccessfulUpload,
      handleMultipleUploadError,
      handleMultipleUploadProgress,
    ],
  )

  const allDocumentsSubmittedOrExpired = useMemo(
    () =>
      documents.every(
        (doc) =>
          checkDocumentDeadline(doc).isPassed || isDocumentSubmitted(doc.label),
      ),
    [documents, submittedDocuments],
  )

  const actions = useMemo(
    () =>
      allDocumentsSubmittedOrExpired
        ? []
        : [
            {
              text:
                documents.length > 1
                  ? t('positionForm.documentUpload.submitDocuments')
                  : t('positionForm.documentUpload.submitDocument'),
              field: 'submit',
              onClick: handleSubmit,
            },
          ],
    [allDocumentsSubmittedOrExpired, documents.length, t, handleSubmit],
  )

  useEffect(() => {
    apiTokens
      .getSingle(token)
      .then((res) => {
        setDocumentSubmissionDeadline(res.expirationDate || '')

        if (exp < (new Date().getTime() + 1) / 1000) {
          if (isRenderForm) {
            setMessage(t('positionForm.documentUpload.linkExpired'))
            setMessageType('error')
            setShowNotification(true)
            setIsRenderForm(false)
          }
        }
      })
      .catch(() => {
        setMessage(t('positionForm.documentUpload.linkNotFound'))
        setMessageType('error')
        setShowNotification(true)
        setIsRenderForm(false)
      })
  }, [token, t, exp, isRenderForm])

  useEffect(() => {
    if (!isLoadingDocuments && isRenderForm) {
      const allDeadlinesPassed = documents.every(
        (doc) => checkDocumentDeadline(doc).isPassed,
      )
      const allSubmittedOrExpired = documents.every(
        (doc) =>
          checkDocumentDeadline(doc).isPassed || isDocumentSubmitted(doc.label),
      )

      if (allDeadlinesPassed) {
        setMessage(t('positionForm.documentUpload.allDeadlinesPassed'))
        setMessageType('error')
        setShowNotification(true)
        setIsRenderForm(false)
      } else if (allSubmittedOrExpired) {
        setMessage(t('positionForm.documentUpload.allDocumentsSubmitted'))
        setMessageType('success')
        setShowNotification(true)
        setIsRenderForm(false)
      }
    }
  }, [isLoadingDocuments, submittedDocuments, documents, isRenderForm, t])

  if (showNotification) {
    const handleBackToForm = () => {
      setShowNotification(false)
      setIsRenderForm(true)
      apiCandidates
        .getDocumentsByToken(candidate.id, token)
        .then((response) => {
          const responseData = response.data || response
          const documentsList = responseData.documents || []
          const submittedDocs = documentsList.map((doc) => doc.name)
          setSubmittedDocuments(submittedDocs)
          setAttachments(responseData.attachments || [])
        })
        .catch(() => {
          setSubmittedDocuments([])
          setAttachments([])
        })
    }

    const allComplete = documents.every(
      (doc) =>
        checkDocumentDeadline(doc).isPassed || isDocumentSubmitted(doc.label),
    )

    return (
      <NotificationBoxPage messageType={messageType} message={message}>
        {!allComplete && messageType === 'success' && (
          <div className="d-flex justify-content-center mt-3">
            <PrimaryButton
              onClick={handleBackToForm}
              width="auto"
              color={theme.brandColorOrange}
              backgroundcolor="transparent"
              hovercolor="transparent"
            >
              {t('positionForm.documentUpload.uploadMoreDocuments')}
            </PrimaryButton>
          </div>
        )}
      </NotificationBoxPage>
    )
  }

  const candidateInitials = (candidate.name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')

  return isRenderForm && !isLoadingDocuments ? (
    <UploadPageBackground>
      {/* ========header */}
      <UploadStickyHeader>
        <Col xs="auto">
          <img height="auto" width="150px" src={FullLogo} alt="" />
        </Col>
        <Col />
        <Col xs="auto" className="mt-3">
          <LanguagesDropdown />
        </Col>
      </UploadStickyHeader>
      <Row className="mt-4 pb-5 justify-content-center">
        <Col xs={12} sm={11} md={8} lg={6} xl={5}>
          <InfoCard className="px-4 pt-4 pb-4">
            <div className="text-center mb-3">
              <UploadHeaderIconBox className="d-inline-flex align-items-center justify-content-center mb-3">
                <FileText
                  width="26"
                  height="26"
                  color={theme.brandColorOrange}
                />
              </UploadHeaderIconBox>
              <Typography
                size="24px"
                bold
                color={theme.talentinoBlack}
                className="d-block mb-2"
              >
                {t('positionForm.documentUpload.documentSubmission')}
              </Typography>
              <Typography size="14px" color={theme.talentinoSecondaryGray}>
                {t('positionForm.documentUpload.submissionSubtitle')}
              </Typography>
            </div>

            <UploadDivider />

            <UploadInfoPanel className="d-flex justify-content-between align-items-center gap-3 p-3 mb-4">
              <div className="d-flex align-items-center gap-3">
                <UploadInitialsCircle className="d-flex align-items-center justify-content-center flex-shrink-0">
                  <Typography size="14px" bold color={theme.white}>
                    {candidateInitials}
                  </Typography>
                </UploadInitialsCircle>
                <div className="d-flex flex-column">
                  <Typography
                    size="11px"
                    color={theme.talentinoSecondaryGray}
                    className="d-block"
                  >
                    {t('general.sendTo')}
                  </Typography>
                  <Typography size="15px" bold color={theme.talentinoBlack}>
                    {candidate.name}
                  </Typography>
                </div>
              </div>
              <div className="d-flex gap-3">
                <UploadCalendarCircle className="d-flex align-items-center justify-content-center border flex-shrink-0">
                  <CalendarSquare
                    width="16"
                    height="16"
                    color={theme.brandColorOrange}
                  />
                </UploadCalendarCircle>
                <div className="d-flex flex-column">
                  <Typography
                    size="11px"
                    color={theme.talentinoSecondaryGray}
                    className="d-block"
                  >
                    {t('general.DocumentSubmissionDeadline')}
                  </Typography>
                  <Typography size="15px" bold color={theme.talentinoBlack}>
                    {documentSubmissionDeadline}
                  </Typography>
                </div>
              </div>
            </UploadInfoPanel>
            {attachments.length > 0 && (
              <UploadAttachmentsBox className="mb-4 p-3">
                <Typography
                  size="13px"
                  bold
                  color={theme.talentinoBlue}
                  className="d-inline-flex align-items-center gap-2 mb-2"
                >
                  <ExclamationCircle
                    color={theme.talentinoBlue}
                    width="16px"
                    height="16px"
                  />
                  {t('positionForm.documentUpload.referenceTitle')}
                </Typography>
                <Typography
                  size="12px"
                  color={theme.talentinoTextGray}
                  className="d-block mb-3"
                >
                  {t('positionForm.documentUpload.referenceInstruction')}
                </Typography>

                <div className="d-flex flex-wrap gap-2">
                  {attachments.map((attachment, index) => (
                    <UploadAttachmentItem
                      key={index}
                      className="d-flex align-items-center py-2 px-3"
                    >
                      <FileText width="16" height="16" className="me-2" />
                      <div className="me-3">
                        <Typography
                          size="12px"
                          bold
                          color={theme.talentinoBlack}
                          className="d-block"
                        >
                          {attachment.name}
                        </Typography>
                      </div>
                      <UploadAttachmentDownloadLink
                        href={attachment?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('positionForm.documentUpload.preview')}
                      >
                        <Download
                          width="14"
                          height="14"
                          color={theme.talentinoBlue}
                        />
                      </UploadAttachmentDownloadLink>
                    </UploadAttachmentItem>
                  ))}
                </div>
              </UploadAttachmentsBox>
            )}

            <AbstractForm
              inputs={inputs}
              schema={schema}
              actions={actions}
              stopLoading={stopMultipleUploadFormLoading}
            >
              <div className="mt-3">
                <ProgressBar
                  ref={{
                    progressRef,
                    errorRef,
                  }}
                />
              </div>
            </AbstractForm>

            <div className="text-center mt-3">
              <Typography size="11px" color={theme.talentinoSecondaryGray}>
                {t('positionForm.documentUpload.privacyNote')}
              </Typography>
            </div>
          </InfoCard>
        </Col>
      </Row>
    </UploadPageBackground>
  ) : (
    <UploadLoadingWrapper className="d-flex justify-content-center align-items-center">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </UploadLoadingWrapper>
  )
}

export default UploadRequestedDocuments
